import React, { Suspense, lazy } from "react"
import { Route, Routes } from "react-router-dom"
import { FacilityProvider } from "src/context/FacilityContext"
import { UserProvider, useUser } from "src/context/UserContext"
import { isAuthenticated, redirectToLogin } from "src/services/authService"
import ErrorBoundary from "src/components/ErrorBoundary"
import OfflineBanner from "src/components/OfflineBanner"
import RouteAnnouncer from "src/components/RouteAnnouncer"
import Callback from "src/pages/Callback"
import BasePattern from "src/patterns/BasePattern"
import { Alert } from "@amzn/lcars-core-ui-components"
import Loader from "@amzn/meridian/loader"
import Column from "@amzn/meridian/column"
import { PERMISSION } from "src/types/user"
import type { Permission } from "src/types/user"

// Route-level code splitting — each screen is its own chunk so the initial
// bundle stays small on cheap devices / weak Wi-Fi. Callback stays eager (it is
// the OAuth landing and must render instantly).
const Home = lazy(() => import("src/pages/Home"))
const CategoryMenu = lazy(() => import("src/pages/CategoryMenu"))
const Lookup = lazy(() => import("src/pages/Lookup"))
const ReceiveScanPO = lazy(() => import("src/pages/ReceiveScanPO"))
const ReceiveLines = lazy(() => import("src/pages/ReceiveLines"))
const ReceiveConfirm = lazy(() => import("src/pages/ReceiveConfirm"))
const InventoryByLocation = lazy(() => import("src/pages/InventoryByLocation"))
const InventoryByItem = lazy(() => import("src/pages/InventoryByItem"))
const InventoryAdjust = lazy(() => import("src/pages/InventoryAdjust"))
const InventoryHold = lazy(() => import("src/pages/InventoryHold"))
const InventoryCycleCount = lazy(() => import("src/pages/InventoryCycleCount"))
const ChangeStockStatus = lazy(() => import("src/pages/inventory/ChangeStockStatus"))
const ReceiveByShipment = lazy(() => import("src/pages/receiving/ReceiveByShipment"))
const PickDelivery = lazy(() => import("src/pages/PickDelivery"))
const PickProduction = lazy(() => import("src/pages/PickProduction"))
const PickComplete = lazy(() => import("src/pages/pick/PickComplete"))
const PickDestination = lazy(() => import("src/pages/pick/PickDestination"))
const PickTicketSelect = lazy(() => import("src/pages/pick/PickTicketSelect"))
const PickConfirmLines = lazy(() => import("src/pages/pick/PickConfirmLines"))
const PickBatch = lazy(() => import("src/pages/pick/PickBatch"))
const ProductionConsume = lazy(() => import("src/pages/ProductionConsume"))
const ProductionReport = lazy(() => import("src/pages/ProductionReport"))

const ScreenLoader: React.FC = () => (
  <Column alignmentHorizontal="center" spacingInset="500">
    <Loader />
  </Column>
)

const App: React.FC = () => (
  <Routes>
    <Route path="/callback" element={<Callback />} />
    <Route
      path="/*"
      element={
        <AuthGuard>
          <ErrorBoundary>
            <OfflineBanner />
            <UserProvider>
              <FacilityProvider>
                <RouteAnnouncer />
                <Suspense fallback={<ScreenLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/category/:category" element={<CategoryMenu />} />
                  <Route path="/lookup" element={<Lookup />} />
                  {/* Receive */}
                  <Route
                    path="/receive"
                    element={
                      <PermissionGate required={PERMISSION.RECEIPT_CREATE}>
                        <ReceiveScanPO />
                      </PermissionGate>
                    }
                  />
                  <Route
                    path="/receive/lines"
                    element={
                      <PermissionGate required={PERMISSION.RECEIPT_CREATE}>
                        <ReceiveLines />
                      </PermissionGate>
                    }
                  />
                  <Route
                    path="/receive/confirm"
                    element={
                      <PermissionGate required={PERMISSION.RECEIPT_CREATE}>
                        <ReceiveConfirm />
                      </PermissionGate>
                    }
                  />
                  <Route
                    path="/receive/by-shipment"
                    element={
                      <PermissionGate required={PERMISSION.RECEIPT_CREATE}>
                        <ReceiveByShipment />
                      </PermissionGate>
                    }
                  />
                  {/* Inventory */}
                  <Route path="/inventory/by-location" element={<InventoryByLocation />} />
                  <Route path="/inventory/by-item" element={<InventoryByItem />} />
                  <Route path="/inventory/adjust" element={<InventoryAdjust />} />
                  <Route path="/inventory/hold" element={<InventoryHold />} />
                  <Route path="/inventory/count" element={<InventoryCycleCount />} />
                  <Route path="/inventory/change-status" element={<ChangeStockStatus />} />
                  {/* Pick */}
                  <Route path="/pick/delivery" element={<PickDelivery />} />
                  <Route path="/pick/production" element={<PickProduction />} />
                  <Route path="/pick/destination/:pickTicketId" element={<PickDestination />} />
                  <Route path="/pick/confirm/:pickTicketId" element={<PickConfirmLines />} />
                  <Route path="/pick/complete/:pickTicketId" element={<PickComplete />} />
                  <Route path="/pick/tickets" element={<PickTicketSelect />} />
                  <Route path="/pick-batches/:pickBatchId" element={<PickBatch />} />
                  {/* Production */}
                  <Route path="/production/consume" element={<ProductionConsume />} />
                  <Route path="/production/report" element={<ProductionReport />} />
                </Routes>
                </Suspense>
              </FacilityProvider>
            </UserProvider>
          </ErrorBoundary>
        </AuthGuard>
      }
    />
  </Routes>
)

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const skipAuth = (import.meta.env.VITE_SKIP_AUTH as string | undefined) === "true"
  if (skipAuth) return <>{children}</>

  if (!isAuthenticated()) {
    // Save the current path so Callback can restore it after login
    sessionStorage.setItem("eclipse_hhu_return_to", window.location.pathname + window.location.search)
    redirectToLogin()
    return null
  }
  return <>{children}</>
}

const PermissionGate: React.FC<{ required: Permission; children: React.ReactNode }> = ({
  required,
  children,
}) => {
  const { hasPermission } = useUser()
  if (!hasPermission(required)) {
    return (
      <BasePattern title="Access denied">
        <Alert
          type="error"
          primaryText="You don't have permission for this action. Ask your lead to grant you the right role."
        />
      </BasePattern>
    )
  }
  return <>{children}</>
}

export default App
